import type { GameSession, SessionState } from "../net/session";
import {
  getMasterVolume,
  resumeAudio,
  setMasterVolume,
  startMenuMusic,
} from "../game/audio";
import { ABILITIES, ABILITY_COUNT, ABILITY_PICKS } from "../game/abilities";
import { playUiClickFx } from "./clickFx";

export type TutorialStage = "abilities" | "duel" | null;

export type UiCallbacks = {
  onHost: () => void;
  onJoin: (code: string) => void;
  onQueue: () => void;
  onCancelQueue: () => void;
  onReady: () => void;
  onRematch: () => void;
  onLeave: () => void;
  onTutorial: () => void;
  onTutorialReady: () => void;
  onTutorialQuit: () => void;
  onTutorialRetry: () => void;
  onTutorialContinue: () => void;
};

export class Screens {
  private root: HTMLElement;
  private session: GameSession;
  private cbs: UiCallbacks;
  private inDuelUi = false;
  private started = false;
  private queueDotsTimer: number | null = null;
  private queueDotsFrame = 0;
  private selectedAbilities = new Set<number>();
  /** Rematch was pressed — the ready screen re-picks abilities for next round. */
  private rematchPending = false;
  private tutorialStage: TutorialStage = null;
  private tutorialGateOpen = false;
  /** Blocks stacked menu clicks while the bullet-hole FX plays. */
  private clickFxBusy = false;

  constructor(root: HTMLElement, session: GameSession, cbs: UiCallbacks) {
    this.root = root;
    this.session = session;
    this.cbs = cbs;
    session.onState((s) => this.render(s));
  }

  setInDuel(active: boolean): void {
    this.inDuelUi = active;
    this.render(this.session.getState());
  }

  /** Ability indices chosen for the current round. */
  getSelectedAbilities(): number[] {
    return [...this.selectedAbilities].sort((a, b) => a - b);
  }

  isTutorialActive(): boolean {
    return this.tutorialStage !== null;
  }

  setTutorialStage(stage: TutorialStage): void {
    this.tutorialStage = stage;
    this.tutorialGateOpen = false;
    if (stage === "abilities") {
      this.selectedAbilities.clear();
      this.rematchPending = false;
      this.inDuelUi = false;
    } else if (stage === "duel") {
      this.inDuelUi = true;
    } else {
      this.inDuelUi = false;
    }
    this.render(this.session.getState());
  }

  isTutorialGateOpen(): boolean {
    return this.tutorialGateOpen;
  }

  /** Centered coaching modal; game should stay paused until Continue. */
  showTutorialGate(tip: string): void {
    this.tutorialGateOpen = true;
    this.root.innerHTML = `
      <div class="tutorial-gate" style="pointer-events:auto">
        <div class="panel tutorial-gate-panel">
          <p class="tutorial-gate-text">${escapeHtml(tip)}</p>
          <div class="btn-row">
            <button type="button" data-act="tutorial-continue">Ready</button>
          </div>
        </div>
      </div>
    `;
    this.bind();
  }

  clearTutorialGate(): void {
    this.tutorialGateOpen = false;
    if (this.tutorialStage === "duel" && this.inDuelUi) {
      this.root.innerHTML = "";
    } else if (this.tutorialStage === "abilities") {
      this.render(this.session.getState());
    }
  }

  showResult(title: string, flavor: string): void {
    this.root.innerHTML = `
      <div class="panel result-panel" style="pointer-events:auto">
        <h2 class="result-title">${escapeHtml(title)}</h2>
        <p class="result-flavor">${escapeHtml(flavor)}</p>
        <div class="btn-row">
          <button type="button" data-act="rematch">Rematch</button>
          <button type="button" class="secondary" data-act="leave">Run Away</button>
        </div>
      </div>
    `;
    this.bind();
  }

  showTutorialResult(title: string, flavor: string): void {
    this.root.innerHTML = `
      <div class="panel result-panel" style="pointer-events:auto">
        <h2 class="result-title">${escapeHtml(title)}</h2>
        <p class="result-flavor">${escapeHtml(flavor)}</p>
        <div class="btn-row">
          <button type="button" data-act="tutorial-retry">Try Again</button>
          <button type="button" class="secondary" data-act="tutorial-quit">Back to Menu</button>
        </div>
      </div>
    `;
    this.bind();
  }

  private abilityGridHtml(): string {
    let cells = "";
    for (let i = 0; i < ABILITY_COUNT; i++) {
      const on = this.selectedAbilities.has(i);
      const def = ABILITIES[i];
      const label = def?.icon
        ? `<img class="ability-icon" src="${def.icon}" alt="${escapeHtml(def.title)}" draggable="false" />`
        : String(i + 1);
      cells += `<button type="button" class="ability${on ? " selected" : ""}" data-act="ability" data-ability="${i}" aria-label="${escapeHtml(def?.title ?? `Ability ${i + 1}`)}" aria-pressed="${on}">${label}</button>`;
    }
    return cells;
  }

  private abilityTooltipHtml(): string {
    return `
      <div class="ability-tooltip" hidden>
        <div class="ability-tooltip-title"></div>
        <div class="ability-tooltip-desc"></div>
      </div>
    `;
  }

  /** Ready screen: pick a loadout, then ready up. Shared by first round and rematch. */
  private readyPanelHtml(): string {
    const canReady = this.selectedAbilities.size === ABILITY_PICKS;
    return `
      <div class="panel ready-panel" style="pointer-events:auto">
        <h2>Choose ${ABILITY_PICKS} Tricks</h2>
        <div class="ability-grid">${this.abilityGridHtml()}</div>
        <div class="btn-row">
          <button type="button" data-act="ready"${canReady ? "" : " disabled"}>I'm Ready</button>
          <button type="button" class="secondary" data-act="leave">Run Away</button>
        </div>
        ${this.abilityTooltipHtml()}
      </div>
    `;
  }

  private tutorialAbilitiesHtml(): string {
    const canReady = this.selectedAbilities.size === ABILITY_PICKS;
    return `
      <div class="panel ready-panel" style="pointer-events:auto">
        <h2>Choose ${ABILITY_PICKS} Tricks</h2>
        <div class="ability-grid">${this.abilityGridHtml()}</div>
        <div class="btn-row">
          <button type="button" data-act="tutorial-ready"${canReady ? "" : " disabled"}>I'm Ready</button>
          <button type="button" class="secondary" data-act="tutorial-quit">Back</button>
        </div>
        ${this.abilityTooltipHtml()}
      </div>
    `;
  }

  private toggleAbility(idx: number): void {
    if (this.selectedAbilities.has(idx)) {
      this.selectedAbilities.delete(idx);
    } else if (this.selectedAbilities.size < ABILITY_PICKS) {
      this.selectedAbilities.add(idx);
    } else {
      return;
    }
    this.refreshAbilityUi();
  }

  /** Update selection styling in place so listeners stay bound. */
  private refreshAbilityUi(): void {
    this.root.querySelectorAll("[data-ability]").forEach((el) => {
      const idx = Number((el as HTMLElement).dataset.ability);
      const on = this.selectedAbilities.has(idx);
      el.classList.toggle("selected", on);
      el.setAttribute("aria-pressed", String(on));
    });
    const canReady = this.selectedAbilities.size === ABILITY_PICKS;
    for (const act of ["ready", "tutorial-ready"]) {
      const ready = this.root.querySelector(
        `[data-act='${act}']`
      ) as HTMLButtonElement | null;
      if (ready) ready.disabled = !canReady;
    }
  }

  private render(state: SessionState): void {
    this.stopQueueDots();

    // Tutorial UI wins while active (session stays idle).
    if (this.tutorialStage === "abilities") {
      if (this.tutorialGateOpen) return;
      this.root.innerHTML = this.tutorialAbilitiesHtml();
      this.bind();
      return;
    }
    if (this.tutorialStage === "duel") {
      if (this.tutorialGateOpen) return;
      if (this.inDuelUi) {
        this.root.innerHTML = "";
        return;
      }
      this.root.innerHTML = this.tutorialAbilitiesHtml();
      this.bind();
      return;
    }

    if (state.phase !== "p2p") {
      this.selectedAbilities.clear();
      this.rematchPending = false;
    }

    if (!this.started) {
      this.root.innerHTML = `
        <div class="start-screen">
          <button type="button" class="play-btn" data-act="start" aria-label="Play">
            <svg viewBox="0 0 64 64" width="64" height="64" aria-hidden="true">
              <circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" stroke-width="3" />
              <polygon points="23,18 23,46 45,32" fill="currentColor" />
            </svg>
          </button>
        </div>
      `;
      this.bind();
      return;
    }

    if (this.inDuelUi && state.phase === "p2p") {
      this.root.innerHTML = "";
      return;
    }

    switch (state.phase) {
      case "idle":
        this.showMainMenu();
        return;
      case "connecting":
        this.root.innerHTML = `
          <div class="panel">
            <h2>Connecting…</h2>
            <p class="status">Talking to the signaling server / establishing peer link…</p>
          </div>
        `;
        break;
      case "queued":
        this.root.innerHTML = `
          <div class="panel">
            <h2>Quick Match</h2>
            <p class="queue-wait">Waiting for another gunslinger<span data-queue-dots>...</span></p>
            <div class="btn-row">
              <button type="button" class="secondary" data-act="cancel-queue">Cancel</button>
            </div>
          </div>
        `;
        this.startQueueDots();
        break;
      case "lobby":
        this.root.innerHTML = `
          <div class="panel lobby-panel">
            <h2>${state.role === "host" ? "Your Lobby" : "Joined Lobby"}</h2>
            <p>Share this code with your pardner</p>
            <div class="code-display">${state.code}</div>
            ${
              state.peerPresent
                ? `<p class="status">Opponent connected — establishing peer link…</p>`
                : ""
            }
            <div class="btn-row">
              <button type="button" class="secondary" data-act="lobby-back">Back</button>
            </div>
          </div>
        `;
        break;
      case "p2p":
        this.root.innerHTML = this.readyPanelHtml();
        break;
      case "error": {
        const ranAway = state.message === "Your opponent ran away";
        this.root.innerHTML = `
          <div class="panel ${ranAway ? "runaway-panel" : ""}">
            <h2 class="${ranAway ? "result-title" : "trouble-title"}">${ranAway ? "You win!" : "TROUBLE IN TOWN"}</h2>
            <p class="${ranAway ? "runaway-msg" : "error"}">${escapeHtml(state.message)}</p>
            <div class="btn-row">
              <button type="button" data-act="leave">Back to Menu</button>
            </div>
          </div>
        `;
        break;
      }
    }
    this.bind();
  }

  private bind(): void {
    this.root.querySelectorAll("[data-act]").forEach((el) => {
      el.addEventListener("click", (ev) => {
        const act = (el as HTMLElement).dataset.act;
        if (!act) return;

        // Ability picks and the splash play button stay instant.
        if (act === "ability") {
          resumeAudio();
          this.toggleAbility(Number((el as HTMLElement).dataset.ability));
          return;
        }
        if (act === "start") {
          this.handleAct(act);
          return;
        }

        if (this.clickFxBusy) return;
        this.clickFxBusy = true;
        const { clientX, clientY } = ev as MouseEvent;
        void playUiClickFx(clientX, clientY).then(() => {
          this.clickFxBusy = false;
          this.handleAct(act);
        });
      });
    });
    this.bindAbilityTooltips();
  }

  private handleAct(act: string): void {
    if (act === "start") {
      resumeAudio();
      startMenuMusic();
      this.started = true;
      this.render(this.session.getState());
      return;
    }
    resumeAudio();
    // Don't yank duel / result audio back to the menu theme.
    if (
      act !== "tutorial-continue" &&
      act !== "tutorial-ready" &&
      act !== "ready" &&
      act !== "rematch"
    ) {
      startMenuMusic();
    }
    if (act === "host") this.cbs.onHost();
    if (act === "queue") this.cbs.onQueue();
    if (act === "cancel-queue") this.cbs.onCancelQueue();
    if (act === "ready") {
      if (this.rematchPending) {
        this.rematchPending = false;
        this.cbs.onRematch();
      } else {
        this.cbs.onReady();
      }
    }
    if (act === "rematch") {
      // Re-pick abilities before the next round
      this.rematchPending = true;
      this.selectedAbilities.clear();
      this.root.innerHTML = this.readyPanelHtml();
      this.bind();
      return;
    }
    if (act === "leave") {
      this.rematchPending = false;
      this.selectedAbilities.clear();
      this.cbs.onLeave();
    }
    if (act === "lobby-back") {
      this.cbs.onLeave();
      this.showCustomMenu();
      return;
    }
    if (act === "play-menu") {
      this.showPlayMenu();
      return;
    }
    if (act === "custom-menu") {
      this.showCustomMenu();
      return;
    }
    if (act === "main-menu") {
      this.showMainMenu();
      return;
    }
    if (act === "tutorial") {
      this.cbs.onTutorial();
      return;
    }
    if (act === "tutorial-ready") {
      this.cbs.onTutorialReady();
      return;
    }
    if (act === "tutorial-quit") {
      this.cbs.onTutorialQuit();
      return;
    }
    if (act === "tutorial-retry") {
      this.cbs.onTutorialRetry();
      return;
    }
    if (act === "tutorial-continue") {
      this.cbs.onTutorialContinue();
      return;
    }
    if (act === "join-form") this.showJoinForm();
    if (act === "settings") this.showSettings();
    if (act === "join-go") {
      const input = this.root.querySelector("#join-code") as HTMLInputElement | null;
      const code = input?.value.trim() ?? "";
      if (code) this.cbs.onJoin(code);
      return;
    }
    if (act === "back") {
      // Join form / settings back
      if (this.root.querySelector("#join-code")) {
        this.showCustomMenu();
      } else {
        this.showMainMenu();
      }
    }
  }

  private bindAbilityTooltips(): void {
    const tip = this.root.querySelector(".ability-tooltip") as HTMLElement | null;
    if (!tip) return;
    const titleEl = tip.querySelector(".ability-tooltip-title") as HTMLElement;
    const descEl = tip.querySelector(".ability-tooltip-desc") as HTMLElement;
    const gap = 10;

    const hide = () => {
      tip.hidden = true;
    };

    this.root.querySelectorAll("[data-ability]").forEach((el) => {
      const btn = el as HTMLElement;
      btn.addEventListener("mouseenter", () => {
        const idx = Number(btn.dataset.ability);
        const def = ABILITIES[idx];
        if (!def) return;
        titleEl.textContent = def.title;
        descEl.textContent = def.description;
        tip.hidden = false;

        const r = btn.getBoundingClientRect();
        const tw = tip.offsetWidth;
        const th = tip.offsetHeight;
        const col = idx % 3; // 0 left, 1 mid, 2 right
        const row = Math.floor(idx / 3); // 0 top, 1 bottom

        let left: number;
        let top: number;
        if (col === 0) {
          // Left column → tooltip to the left
          left = r.left - tw - gap;
          top = r.top + r.height / 2 - th / 2;
        } else if (col === 2) {
          // Right column → tooltip to the right
          left = r.right + gap;
          top = r.top + r.height / 2 - th / 2;
        } else if (row === 0) {
          // Middle top → above
          left = r.left + r.width / 2 - tw / 2;
          top = r.top - th - gap;
        } else {
          // Middle bottom → below
          left = r.left + r.width / 2 - tw / 2;
          top = r.bottom + gap;
        }

        left = Math.max(8, Math.min(window.innerWidth - tw - 8, left));
        top = Math.max(8, Math.min(window.innerHeight - th - 8, top));
        tip.style.left = `${left}px`;
        tip.style.top = `${top}px`;
      });
      btn.addEventListener("mouseleave", hide);
      btn.addEventListener("blur", hide);
    });
  }

  private startQueueDots(): void {
    this.stopQueueDots();
    const frames = ["...", "..", ".", ".."];
    this.queueDotsFrame = 0;
    const el = () => this.root.querySelector("[data-queue-dots]");
    const tick = () => {
      const dots = el();
      if (!dots) {
        this.stopQueueDots();
        return;
      }
      dots.textContent = frames[this.queueDotsFrame % frames.length]!;
      this.queueDotsFrame += 1;
    };
    tick();
    this.queueDotsTimer = window.setInterval(tick, 400);
  }

  private stopQueueDots(): void {
    if (this.queueDotsTimer !== null) {
      window.clearInterval(this.queueDotsTimer);
      this.queueDotsTimer = null;
    }
  }

  private showMainMenu(): void {
    this.root.innerHTML = `
      <div class="panel">
        <h1>Cowboy Countdown</h1>
        <div class="btn-row">
          <button type="button" data-act="play-menu">Play</button>
          <button type="button" data-act="tutorial">Tutorial</button>
          <button type="button" data-act="settings">Settings</button>
        </div>
      </div>
    `;
    this.bind();
  }

  private showPlayMenu(): void {
    this.root.innerHTML = `
      <div class="panel">
        <h1>Cowboy Countdown</h1>
        <div class="btn-row">
          <button type="button" data-act="queue">Quick Match</button>
          <button type="button" data-act="custom-menu">Custom Match</button>
          <button type="button" class="secondary" data-act="main-menu">Back</button>
        </div>
      </div>
    `;
    this.bind();
  }

  private showCustomMenu(): void {
    this.root.innerHTML = `
      <div class="panel">
        <h1>Cowboy Countdown</h1>
        <div class="btn-row">
          <button type="button" data-act="host">Host Lobby</button>
          <button type="button" data-act="join-form">Join Lobby</button>
          <button type="button" class="secondary" data-act="play-menu">Back</button>
        </div>
      </div>
    `;
    this.bind();
  }

  private showJoinForm(): void {
    this.root.innerHTML = `
      <div class="panel">
        <h2>Join Lobby</h2>
        <input type="text" id="join-code" maxlength="6" placeholder="CODE" autocomplete="off" />
        <div class="btn-row">
          <button type="button" data-act="join-go">Join</button>
          <button type="button" class="secondary" data-act="back">Back</button>
        </div>
      </div>
    `;
    const input = this.root.querySelector("#join-code") as HTMLInputElement;
    input.focus();
    input.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      resumeAudio();
      startMenuMusic();
      const code = input.value.trim();
      if (code) this.cbs.onJoin(code);
    });
    this.bind();
  }

  private showSettings(): void {
    const pct = Math.round(getMasterVolume() * 100);
    this.root.innerHTML = `
      <div class="panel settings-panel">
        <h2>Settings</h2>
        <label class="volume-row" for="volume-slider">
          <span class="volume-label">Volume</span>
          <input
            type="range"
            id="volume-slider"
            min="0"
            max="100"
            step="1"
            value="${pct}"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow="${pct}"
          />
          <span class="volume-pct" data-volume-pct>${pct}%</span>
        </label>
        <div class="btn-row">
          <button type="button" class="secondary" data-act="back">Back</button>
        </div>
      </div>
    `;
    const slider = this.root.querySelector("#volume-slider") as HTMLInputElement;
    const pctEl = this.root.querySelector("[data-volume-pct]") as HTMLElement;
    const apply = () => {
      const v = Number(slider.value) / 100;
      setMasterVolume(v);
      const shown = Math.round(v * 100);
      pctEl.textContent = `${shown}%`;
      slider.setAttribute("aria-valuenow", String(shown));
    };
    slider.addEventListener("input", apply);
    this.bind();
  }
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
